/**
 * PendingApprovals Page
 * Manager dashboard for reviewing and approving pending OKRs
 */

import React, { useState } from 'react';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { useUrlState } from '../../hooks/useUrlState';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, Eye } from 'lucide-react';
import { usePendingApprovals, useBulkApprove } from '../../hooks';
import { Card, Button, Input, EmptyState, LoadingSpinner, Avatar } from '../../components/common';
import { ApprovalActionModal } from '../../components/okr';
import type { OKR } from '../../types/okr';

export const PendingApprovals: React.FC = () => {
  const openWithReturn = useOpenWithReturn();
  // In the URL, so opening an OKR and coming Back keeps the search.
  const [view, setView] = useUrlState({ q: '' });
  const searchQuery = view.q;
  const setSearchQuery = (q: string) => setView({ q });
  const [selectedOKRs, setSelectedOKRs] = useState<string[]>([]);
  const [modalOKR, setModalOKR] = useState<OKR | null>(null);

  const { data: pendingOKRs, isLoading, refetch } = usePendingApprovals();
  const { mutate: bulkApprove, isPending: isBulkApproving } = useBulkApprove();

  // Filter OKRs by search query
  const filteredOKRs = pendingOKRs?.filter((okr) =>
    okr.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    `${okr.owner.firstName} ${okr.owner.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Handle select all checkbox
  const allSelected = filteredOKRs.length > 0 && selectedOKRs.length === filteredOKRs.length;
  const someSelected = selectedOKRs.length > 0 && selectedOKRs.length < filteredOKRs.length;

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedOKRs([]);
    } else {
      setSelectedOKRs(filteredOKRs.map((okr) => okr.id));
    }
  };

  const handleToggleSelect = (okrId: string) => {
    setSelectedOKRs((prev) =>
      prev.includes(okrId) ? prev.filter((id) => id !== okrId) : [...prev, okrId]
    );
  };

  const handleBulkApprove = () => {
    if (selectedOKRs.length === 0) return;

    if (
      window.confirm(
        `Are you sure you want to approve ${selectedOKRs.length} OKR${
          selectedOKRs.length > 1 ? 's' : ''
        }?`
      )
    ) {
      bulkApprove(
        { okrIds: selectedOKRs },
        {
          onSuccess: () => {
            setSelectedOKRs([]);
            refetch();
          },
        }
      );
    }
  };

  const handleOpenModal = (okr: OKR) => {
    setModalOKR(okr);
  };

  const handleCloseModal = () => {
    setModalOKR(null);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const handleViewOKR = (okrId: string) => {
    openWithReturn(`/okrs/${okrId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <div className="bg-white border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Pending Approvals</h1>
            <p className="mt-1 text-sm text-stone-500">
              Review and approve OKRs from your team
            </p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" text="Loading pending approvals..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Pending Approvals</h1>
              <p className="mt-1 text-sm text-stone-500">
                Review and approve OKRs from your team
              </p>
            </div>
            {pendingOKRs && pendingOKRs.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500">
                  {pendingOKRs.length} pending
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Bulk Actions */}
        {pendingOKRs && pendingOKRs.length > 0 && (
          <Card className="p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              {/* Search */}
              <Input
                placeholder="Search by OKR title or owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="sm:max-w-md"
              />

              {/* Bulk Actions */}
              {selectedOKRs.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-stone-500">
                    {selectedOKRs.length} selected
                  </span>
                  <Button
                    variant="primary"
                    onClick={handleBulkApprove}
                    isLoading={isBulkApproving}
                    disabled={isBulkApproving}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Selected
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Empty State */}
        {!pendingOKRs || pendingOKRs.length === 0 ? (
          <Card className="p-12">
            <EmptyState
              icon={CheckCircle}
              title="All caught up!"
              description="No OKRs pending your approval. New approval requests will appear here."
            />
          </Card>
        ) : filteredOKRs.length === 0 ? (
          <Card className="p-12">
            <EmptyState
              title="No results found"
              description="Try adjusting your search query"
            />
          </Card>
        ) : (
          /* Table */
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-200">
                <thead className="bg-sand">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left w-12">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={handleToggleSelectAll}
                        className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                    >
                      OKR
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                    >
                      Owner
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                    >
                      Submitted
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                    >
                      Key Results
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-stone-500"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 bg-white">
                  {filteredOKRs.map((okr) => (
                    <tr key={okr.id} className="hover:bg-sand transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedOKRs.includes(okr.id)}
                          onChange={() => handleToggleSelect(okr.id)}
                          className="h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-md">
                          <p className="text-sm font-medium text-stone-900">{okr.title}</p>
                          {okr.description && (
                            <p className="text-sm text-stone-500 line-clamp-1 mt-1">
                              {okr.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar
                            src={okr.owner.profilePicture}
                            fallbackText={`${okr.owner.firstName} ${okr.owner.lastName}`}
                            size="sm"
                          />
                          <div>
                            <p className="text-sm font-medium text-stone-900">
                              {okr.owner.firstName} {okr.owner.lastName}
                            </p>
                            {okr.owner.department && (
                              <p className="text-xs text-stone-500">{okr.owner.department}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                        {formatDistanceToNow(new Date(okr.updatedAt), { addSuffix: true })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-900">
                        {okr.keyResults?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewOKR(okr.id)}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenModal(okr)}
                          >
                            Review
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Approval Action Modal */}
      {modalOKR && (
        <ApprovalActionModal
          okr={modalOKR}
          isOpen={!!modalOKR}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
